import { defineNuxtRouteMiddleware, useAuth, useOidcClient, useIntercom, useRedirectPath } from '#imports';

const authFlowRouteNames = ['login', 'silent-refresh', 'logout', 'district-picker'];
const isAdminRoute = (path?: string): boolean => path === '/admin' || Boolean(path?.startsWith('/admin/'));

export default defineNuxtRouteMiddleware(async ({ name, path, fullPath }) => {
  const { state } = useAuth();
  const redirectPath = useRedirectPath();
  const oidcClient = await useOidcClient();
  const intercom = useIntercom();
  let user = await oidcClient.getUser();

  if (user?.expired && !authFlowRouteNames.includes(name as string)) {
    const refreshed = await renewSession({ name: name as string, fullPath });
    if (refreshed.outcome === 'logged_out') return abortNavigation();
    // Aborting the first navigation of a client-only app renders a 404.
    // Keep the stored session instead; the next 401 retries the renewal.
    user = refreshed.user ?? user;
  }

  if (!user) {
    if (!authFlowRouteNames.includes(name as string)) {
      await intercom.shutdown();
      redirectPath.value = fullPath;
      await oidcClient.signinRedirect();
      return abortNavigation();
    }
  } else {
    // oidc-client-ts types custom claims as `unknown`; name the shape this check reads.
    const roles = user?.profile?.roles as { user_management?: string; rooms?: string } | undefined;
    const isAdmin = roles?.user_management === 'admin';
    const isGuardianOrStudent = ['guardian', 'student'].includes(roles?.rooms ?? '');
    if (isAdmin || isGuardianOrStudent || isAdminRoute(path ?? fullPath)) {
      await intercom.shutdown();
    } else {
      await intercom.boot();
    }
    state.value.user = user;
    state.value.loggedIn = true;
  }
});
